/**
 * Data Validation Utilities
 *
 * Helper functions to determine if components should render based on data availability.
 * Used for conditional rendering of metric cards, charts, and tables across dashboards.
 */

/**
 * Check if metric data is meaningful (has impressions or clicks > 0)
 * @param value - Can be a number or an object with impressions/clicks properties
 * @returns true if data should be displayed, false otherwise
 */
export const hasMetricData = (value: any): boolean => {
  if (value === null || value === undefined) return false;

  // Handle object format: { impressions: number, clicks: number }
  if (typeof value === 'object') {
    return (value.impressions > 0) || (value.clicks > 0);
  }

  // Handle number format
  return (value > 0);
};

/**
 * Check if chart data has meaningful entries (at least one data point with activity)
 * @param data - Array of chart data points
 * @returns true if chart should be displayed, false otherwise
 */
export const hasChartData = (data: any[]): boolean => {
  if (!data || data.length === 0) return false;

  // Check if at least one data point has impressions or clicks
  return data.some(item =>
    (item.impressions && item.impressions > 0) ||
    (item.clicks && item.clicks > 0)
  );
};

/**
 * Check if table data has entries
 * @param data - Array of table rows
 * @returns true if table should be displayed, false otherwise
 */
export const hasTableData = (data: any[]): boolean => {
  return data && Array.isArray(data) && data.length > 0;
};

/**
 * Count how many metrics have visible data (for debugging/logging)
 * @param summary - Summary object with channel metrics
 * @returns number of channels with data
 */
export const countVisibleMetrics = (summary: any): number => {
  if (!summary) return 0;

  let count = 0;
  Object.values(summary).forEach(value => {
    if (hasMetricData(value)) count++;
  });
  return count;
};
